/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useCallback, useEffect, Fragment } from 'react'
import * as Yup from 'yup'
import { useHistory } from 'react-router-dom'
import { Formik, Field, Form } from 'formik'
import Loading from 'custom/assets/loaders/Loading'
import TermsAndCondition from '../../../components/TermsAndConditions/TermsAndCondition'
import AuthService from '../../../services/auth.service'
import { SignUpSchema } from '../../../schemas/auth'
import InputError from '../../../components/InputError/InputError'
import NeoInput from '../../../components/NeoInput/NeoInput'
import NeoButton from '../../../components/NeoButton/NeoButton'
import WinnersTrophy from 'custom/assets/icons/signup/signup-success'
import {
  DIALOG_KEYS,
  AUTH_KEYS,
  VALIDATION,
  VALIDATION_MESSAGES,
} from '../../../utils/translation-constants'
import { useTranslation } from 'react-i18next'
import AuthLayout from '../auth-layout'
import config from 'custom/config/environment'
import { useSelector } from 'react-redux'
import { getMenuListSelector } from '../../../selectors'

import validationConfig from 'custom/config/validation.config.json'
import SignUpTermsAndCondition from '../../../components/TermsAndConditions/signUpTermsAndCondition'
const signUpConfig = validationConfig['signUp']

export default function SignUp() {
  const [stage, setStage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [passwordPattern, setPasswordPattern] = useState(null)
  const menuConfig = useSelector(getMenuListSelector) || {}
  const [formValues, setFormValues] = useState({
    accountNumber: '',
    phoneNumber: '',
    countryCode: config.COUNTRY_CODE,
    dateOfBirth: '',
    uniqueIdValue: '',
    uniqueIdType: '',
  })
  const [criteria, setCriteria] = useState([])
  const history = useHistory()
  const { t } = useTranslation()
  const [otpRequired, setOtpRequired] = useState(false)
  const [isTermAccepted, setIsTermAccepted] = useState(false)
  const [countryCodesList, setCountryCodesList] = useState([])

  const uniqueIdTypes = {
    PASSPORT: t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.PASSPORT_NO),
    NATIONALID: t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.NATIONAL_ID),
    VOTERID: t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.VOTER_ID),
    DRIVINGLICENCE: t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.DRIVING_LICENCE),
  }

  const verifyAccountNumber = useCallback(async (data, { setSubmitting }) => {
    try {
      setSubmitting(true)
      setLoading(true)

      const response = await AuthService.verifyUserDetails({
        customerId: data.accountNumber,
        countryCode: data.countryCode,
        mobile: data.phoneNumber,
        uniqueIdType: data.uniqueIdType,
        uniqueIdValue: data.uniqueIdValue,
        username: 'DEFAULT',
        dob: data.dateOfBirth,
        relationshipType: 'ACCOUNT',
      })
      setFormValues(data)
      setOtpRequired(response.otpEnable)
      setLoading(false)
      setSubmitting(false)
      setStage(2)
    } catch (err) {
      setLoading(false)
      setSubmitting(false)
    }
  }, [])

  const registerUserSubmit = useCallback(
    async (data, { setSubmitting }) => {
      try {
        setLoading(true)
        setSubmitting(true)
        const response = await AuthService.registerUser({
          mobile: formValues.phoneNumber,
          password: data.password,
          userName: data.userName,
          customerId: formValues.accountNumber,
          otp: otpRequired ? data.otp : '0000',
          countryCode: formValues.countryCode,
          uniqueIdType: formValues.uniqueIdType,
          uniqueIdValue: formValues.uniqueIdValue,
          dob: formValues.dateOfBirth,
          relationshipType: 'ACCOUNT',
        })
        setMessage(response.code + ': ' + response.message)
        setLoading(false)
        setSubmitting(false)
        setStage(4)
      } catch (err) {
        setLoading(false)
        setSubmitting(false)
      }
    },
    [formValues, otpRequired],
  )

  useEffect(() => {
    getPwdCriteria()
    getCountryCodeList()
  }, [])

  const getCountryCodeList = useCallback(async () => {
    try {
      setLoading(true)
      const response = await AuthService.getCountryCodes()
      setCountryCodesList(response.countryCodeList)
      setLoading(false)
    } catch (err) {
      setLoading(false)
    }
  })

  const getPwdCriteria = useCallback(async () => {
    try {
      setLoading(true)
      const response = await AuthService.getPasswordCriteria()
      setCriteria(response.userPasswordCriteriaMap)
      // const pwdMaxLength = response['userPasswordCriteria'][3].value
      // const pwdMinLength = response['userPasswordCriteria'][4].value
      // const pwdMinAlphabetLength = response['userPasswordCriteria'][0].value
      // const pwdMinNumberLength = response['userPasswordCriteria'][1].value
      // const pwdMinSplCharLength = response['userPasswordCriteria'][2].value
      const pwdMaxLength = response['userPasswordCriteriaMap']['MAX_PWD_LENGTH']
      const pwdMinLength = response['userPasswordCriteriaMap']['MIN_PWD_LENGTH']
      const pwdMinAlphabetLength = response['userPasswordCriteriaMap']['ALPHABETS']
      const pwdMinNumberLength = response['userPasswordCriteriaMap']['NUMERICS']
      const pwdMinSplCharLength = response['userPasswordCriteriaMap']['SPECIAL_CHARACTERS']
      const pattern = `(?=^.{${pwdMinLength},${pwdMaxLength}}$)(?=(.*\\d){${pwdMinNumberLength}})(?=(.*[A-Za-z]){${pwdMinAlphabetLength}})(?=(.*[\\+\\- \\= \\[\\]\\{ \\}\\;\\-\\(\\)\\<\\>\\_'!@#$%^&*?\"|/,.:\`~\\\\]){${pwdMinSplCharLength}})(?!.*[\\s])^.*`
      // const pattern1 = `^.*(?=.{8,16})((?=.*[!@#$%^&*()-_=+{};:,<.>]){1})((?=.*d){1})((?=.*[A-Za-z]){2}).*$`
      setPasswordPattern(pattern)
      setLoading(false)
    } catch (err) {
      setLoading(false)
    }
  }, [])

  function getMaxDoB() {
    const today = new Date()
    const dates = [
      today.getFullYear() - config.DOB_MINOR_AGE_VALIDATION,
      today.getMonth() + 1,
      today.getDate() - 1,
    ]
    return `${dates[0]}-${dates[1] < 10 ? '0' + dates[1] : dates[1]}-${
      dates[2] < 10 ? '0' + dates[2] : dates[2]
    }`
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  function handleKeyDown(e) {
    if (e.key === ' ') {
      e.preventDefault()
    }
  }

  function renderForm() {
    return (
      <Formik
        enableReinitialize
        onSubmit={verifyAccountNumber}
        validationSchema={SignUpSchema}
        initialValues={formValues}>
        {({ isSubmitting, errors, touched, setFieldValue, handleChange, values }) => (
          <Form>
            <div className='row'>
              <div className='form-group col-12'>
                <NeoInput
                  tabIndex={'1'}
                  showAsterisk={signUpConfig['isAccountNoRequired']}
                  name='accountNumber'
                  text={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.ACCOUNT_NUMBER)}
                  icon='account_balance'
                  //mode='single'
                  mode='onlyNumber'
                  disabled={isSubmitting}
                  onChange={(value) => {
                    setFieldValue('accountNumber', value?.value)
                  }}
                  // maxLength={13}
                  placeholder={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.ACCOUNT_NUMBER)}
                  value={values.accountNumber}
                />
                <InputError text={errors.accountNumber} touched={touched.accountNumber} />
              </div>

              <div className='form-group col-12'>
                <p className='control-label'>
                  {t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.MOBILE_NUMBER)}{' '}
                  {signUpConfig['isPhoneNoRequired'] ? '*' : ''}
                </p>
                <div className='row '>
                  <div className='col-4 text-center' style={{ marginTop: '-22px' }}>
                    <NeoInput
                      tabIndex={'2'}
                      mode='select'
                      name='countryCode'
                      icon='phone'
                      keyValue="isdCode"
                      value={values.countryCode}
                      list={countryCodesList}
                      onChange={handleChange}
                    />
                  </div>
                  <div
                    className='col-8'
                    style={{ marginTop: '-23px', marginLeft: '-15px', paddingRight: '0px' }}>
                    <NeoInput
                      tabIndex={'3'}
                      name='phoneNumber'
                      icon='phone'
                      mode='onlyNumber'
                      onChange={(value) => {
                        setFieldValue('phoneNumber', value?.value)
                      }}
                      disabled={isSubmitting}
                      placeholder={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.MOBILE_NUMBER)}
                      value={values.phoneNumber}
                      // maxLength={10}
                    />
                  </div>
                </div>
                <InputError text={errors.phoneNumber} touched={touched.phoneNumber} />
              </div>
              <div className='form-group col-12'>
                <NeoInput
                tabIndex={'4'}
                  showAsterisk={signUpConfig['isDOBRequired']}
                  name='dateOfBirth'
                  text={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.DOB)}
                  icon='calendar_today'
                  value={values.dateOfBirth}
                  mode='single'
                  type='date'
                  onChange={handleChange}
                  disabled={isSubmitting}
                  placeholder={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.DOB)}
                  max={getMaxDoB()}
                />
                <InputError text={errors.dateOfBirth} touched={touched.dateOfBirth} />
              </div>
              <div className='form-group col-12'>
                <label className='control-label'>
                  {t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.IDENTITY_TYPE)}{' '}
                  {signUpConfig['isUniqueIdRequired'] ? '*' : ''}
                </label>
                <Field
                tabIndex={'5'}
                  as='select'
                  name='uniqueIdType'
                  className='form-control'
                  onChange={handleChange}>
                  <option value='' />
                  {config.UNIQUE_ID_TYPE.map((option, index) => (
                    <option value={option} key={index}>
                      {uniqueIdTypes[option]}
                    </option>
                  ))}
                </Field>
                <InputError text={errors.uniqueIdType} touched={touched.uniqueIdType} />
              </div>

              <div className='form-group col-12'>
                <NeoInput
                tabIndex={'6'}
                  showAsterisk={signUpConfig['isUniqueIdRequired']}
                  mode='single'
                  text={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.IDENTITY_NO)}
                  name='uniqueIdValue'
                  value={values.uniqueIdValue}
                  icon='perm_identity'
                  onChange={handleChange}
                />
                <InputError text={errors.uniqueIdValue} touched={touched.uniqueIdValue} />
              </div>

              {/* {values.identityType === 'Passport No.' && (
                <div className='form-group col-12'>
                  <NeoInput
                    showAsterisk={signUpConfig['isPassportRequired']}
                    mode='single'
                    text={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.PASSPORTNO)}
                    name='passportNumber'
                    value={values.passportNumber}
                    icon='perm_identity'
                    onChange={handleChange}
                  />
                  <InputError text={errors.passportNumber} touched={touched.passportNumber} />
                </div>
              )}

              {values.identityType === 'National ID' && (
                <div className='form-group col-12'>
                  <NeoInput
                    showAsterisk={signUpConfig['isNRCRequired']}
                    mode='onlyNumber'
                    name='nrcNumber'
                    text={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.NATIONAL_ID_LABEL)}
                    value={values.nrcNumber}
                    icon='perm_identity'
                    onChange={(values) => {
                      setFieldValue('nrcNumber', values.value)
                    }}
                    placeholder={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.NATIONAL_ID_LABEL)}
                  />
                  <InputError text={errors.nrcNumber} touched={touched.nrcNumber} />
                </div>
              )} */}
              <div className='form-group col-12'>
                <SignUpTermsAndCondition
                  tabIndex='7'
                  setIsTermAccepted={setIsTermAccepted}
                  module='REGISTRATION'
                />
              </div>
            </div>

            <div className='my-3' />

            {isSubmitting === false && loading === false ? (
              <NeoButton
                tabIndex='8'
                text={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.BUTTON)}
                mode='newDesign'
                type='submit'
                disabled={!isTermAccepted}
                onClick={(e) => {
                  if (e.key === 'Enter') e.preventDefault()
                }}
              />
            ) : (
              renderLoading()
            )}

            <div className='back-cancel-btn-container'>
            <button  className='back-cancel-btn btn btn-sm new-design-warning-text-color' onClick={() => setStage(0)} tabIndex='9'>
              {' '}
              {t(AUTH_KEYS.FORGOT_PASSWORD_KEYS.RESET_KEYS.BACK)}
            </button>
            </div>

            {/* <div className='my-3 text-center'>
              <button
                onClick={() => setActiveSection(0)}
                className='btn btn-sm btn-link small new-design-warning-text-color font-weight-600'>
                Back To Login
              </button>
            </div> */}
          </Form>
        )}
      </Formik>
    )
  }

  const SignUpSchemaConfirm = Yup.object().shape({
    userName: Yup.string()
    .required(t(VALIDATION.USERNAME))
    .min(4, t(VALIDATION.USERNAMEVALID))
    .max(40, t(VALIDATION.USERNAMEVALID)),
    password: Yup.string()
      .required(t(VALIDATION.PASSWORD))
      .matches(new RegExp(passwordPattern), t(VALIDATION_MESSAGES.PASSWORD_CRITERIA)),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], t(VALIDATION.PASSWORDNOTMATCH))
      .required(t(VALIDATION.CONFIRMNEWPASSWORD)),
    otp: otpRequired ? Yup.string().required(t(VALIDATION.OTP)) : Yup.string().optional(),
  })

  function renderConfirmForm() {
    return (
      <div>
        <Formik
          onSubmit={registerUserSubmit}
          validationSchema={SignUpSchemaConfirm}
          initialValues={{
            userName: '',
            password: '',
            confirmPassword: '',
            otp: '',
          }}>
          {({ isSubmitting, errors, touched, handleChange, setFieldValue }) => (
            <Form>
              <div className='form-group '>
                <NeoInput
                  tabIndex='1'
                  showAsterisk={signUpConfig['isUserNameRequired']}
                  text={t(AUTH_KEYS.SIGN_UP_KEYS.DETAILS_KEYS.USERNAME)}
                  name='userName'
                  mode='single'
                  icon='person'
                  disabled={isSubmitting}
                  onChange={handleChange}
                  onKeyPress={(event) => {
                    if (
                      !(event.charCode >= 65 && event.charCode <= 90) &&
                      !(event.charCode >= 97 && event.charCode <= 122) &&
                      event.charCode !== 32
                    ) {
                      event.preventDefault()
                    }
                  }}
                />
                <InputError text={errors.userName} touched={touched.userName} />
              </div>
              <div className='form-group  my-3'>
                <NeoInput
                  tabIndex='2'
                  showAsterisk={signUpConfig['isNewPasswordRequired']}
                  text={t(AUTH_KEYS.SIGN_UP_KEYS.DETAILS_KEYS.PASSWORD)}
                  type='password'
                  name='password'
                  icon=''
                  mode='sensitiveIcon'
                  onKeyDown={handleKeyDown}
                  disabled={isSubmitting}
                  onChange={handleChange}
                  placeholder={t(AUTH_KEYS.SIGN_UP_KEYS.DETAILS_KEYS.PASSWORD)}
                />
                <InputError text={errors.password} touched={touched.password} />
              </div>
              <div className='form-group  my-3'>
                <NeoInput
                  tabIndex='3'
                  showAsterisk={signUpConfig['isConfirmPasswordRequired']}
                  type='password'
                  name='confirmPassword'
                  text={t(AUTH_KEYS.SIGN_UP_KEYS.DETAILS_KEYS.CONFIRM_PASSWORD)}
                  icon=''
                  mode='sensitiveIcon'
                  disabled={isSubmitting}
                  onChange={handleChange}
                  placeholder={t(AUTH_KEYS.SIGN_UP_KEYS.DETAILS_KEYS.CONFIRM_PASSWORD)}
                />
                <InputError text={errors.confirmPassword} touched={touched.confirmPassword} />
              </div>

              {otpRequired && (
                <div className='form-group my-3'>
                  <NeoInput
                    tabIndex='4'
                    showAsterisk={true}
                    maxLength={4}
                    text={t(AUTH_KEYS.FORGOT_PASSWORD_KEYS.RESET_KEYS.OTP)}
                    type='number'
                    mode='onlyNumber'
                    onChange={(value) => {
                      setFieldValue('otp', value?.value)
                    }}
                    name='otp'
                    disabled={isSubmitting}
                  />
                  <InputError text={errors.otp} touched={touched.otp} />
                </div>
              )}

              <div className='passwordCriteria'>
                <h6>{t(AUTH_KEYS.SIGN_UP_KEYS.DETAILS_KEYS.PASSWORD_CRITERIA)}</h6>
                {/* {criteria.map((value, i) => (
                  <p key={i} className='card-text col'>
                    {value.description} : <b>{value.value}</b>
                  </p>
                ))} */}
                {Object.keys(criteria).map((key, i) => (
                  <p key={i} className='card-text col'>
                    {t(key)} : <b>{criteria[key]}</b>
                  </p>
                ))}
              </div>
              <div className='my-3' />
              {isSubmitting === false ? (
                <NeoButton
                  tabIndex='5'
                  text={t(AUTH_KEYS.SIGN_UP_KEYS.DETAILS_KEYS.BUTTON)}
                  mode='newDesign'
                  type='submit'
                />
              ) : null}
              {isSubmitting === true ? renderLoading() : null}

              <div className='my-4' />
              <div className='back-cancel-btn-container'>
                <button
                  tabIndex='6'
                  onClick={() => {
                    setStage(1)
                  }}
                  className='back-cancel-btn btn btn-sm new-design-warning-text-color '
                  style={{ marginLeft: '170px' }}>
                  {t(AUTH_KEYS.FORGOT_PASSWORD_KEYS.RESET_KEYS.BACK)}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  function renderLanding() {
    return (
      <>
        {menuConfig['newCustomer'] && (
          <div
            tabIndex={'1'}
            className='register-card text-center'
            onClick={() => window.open(config.NEW_CUSTOMER, '_blank')}>
            <div className='new-customer-logo'></div>
            <h3>{t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.LOGIN_KEYS.NEW_CUSTOMER_TAB)}</h3>
            <p>{t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.NEW_CUSTOMER_KEYS.DESCRIPTION)}</p>
          </div>
        )}
        {menuConfig['existingCustomer'] && (
          <div className='register-card text-center' onClick={() => setStage(1)} tabIndex={'2'}>
            <div className='existing-customer-logo'></div>
            <h3>{t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.SUBTITLE_3)}</h3>
            <p>{t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.DESCRIPTION)}</p>
          </div>
        )}

        {menuConfig['walletCustomer'] && (
          <div className='register-card text-center'  onClick={() =>  history.push('/wallet-customer')} tabIndex={'3'}>
            <div className='new-customer-logo'></div>
            <h3>{t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.WALLET_CUSTOMER_KEYS.SUBTITLE)}</h3>
            <p>{t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.WALLET_CUSTOMER_KEYS.DESCRIPTION)}</p>
          </div>
        )}

       <div className='back-cancel-btn-container'>
        <button tabIndex={'3'} className='back-cancel-btn btn btn-sm new-design-warning-text-color ' onClick={() => history.push('/login')}>
          {' '}
          {t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.LOGIN_KEYS.TITLE)}
        </button>
        </div>
      </>
    )
  }

  function renderSuccess() {
    return (
      <div className='row justify-content-center '>
        <WinnersTrophy width='250' height='250' />
        <p className='lead'>{message}</p>
        <div className='my-3 ' />
        <NeoButton
          text={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.LOGIN_KEYS.TITLE)}
          type='submit'
          mode='newDesign'
          onClick={() => {
            history.push('/login')
          }}
        />
      </div>
    )
  }

  const renderSignupStage = () => {
    switch (stage) {
      case 0:
        return renderLanding()
      case 1:
        return renderForm()
      case 2:
        return renderConfirmForm()
      case 4:
        return renderSuccess()
      default:
        return renderLanding()
    }
  }

  return (
    <AuthLayout>
      <div className='container col mt-5 ' style={{ width: '400px' }}>
        <h3 className='register-with-us-tod'>{t(AUTH_KEYS.SIGN_UP_KEYS.TITLE)}</h3>
        <div className='my-2' />
        {renderSignupStage()}
      </div>
    </AuthLayout>
  )
}
